import { faHeart as faHeartOutline } from '@fortawesome/pro-light-svg-icons';
import { faPlay } from '@fortawesome/pro-regular-svg-icons';
import { faHeart, faLockAlt, faSpinner, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NewOutlineButton, PrimaryButton } from 'Components/Buttons';
import { Box, Text } from 'grommet';
import { memo } from 'preact/compat';
import { useMemo, useState } from 'preact/hooks';
import { Link } from 'preact-router';
import { useDispatch } from 'react-redux';
import { buildURL } from 'Shared/fetch';
import { logLandingButton } from 'Shared/ga';
import useFetch from 'use-http';

import { useSelector } from '@/redux/helper';
import { toggleFavoriteHabitat } from '@/redux/slices/user/actions';

import HabitatCardBase from './HabitatCardBase';
import style from './style.scss';

const HabitatCard = ({
  className,
  habitatId,
  zooSlug,
  slug,
  online,
  liveTalk,
  image,
  logo,
  title,
  trailer,
  description,
  onShowTrailer,
  onFavoriteClick,
  isLocked,
  primaryButtonTitle,
  onClickPrimaryButton,
  onClose,
  displayCloseButton = true,
}) => {
  const dispatch = useDispatch();
  const [error, setError] = useState(false);
  const favoriteHabitats = useSelector((state) => state.user.favoriteHabitats);
  const subscription = useSelector((state) => state.user.subscription);
  const isLogged = useSelector((state) => state.user.logged);
  const role = useSelector((state) => state.user.role);
  const isGeneralUser = useMemo(() => ['vip', 'user'].includes(role), [role]);
  const isFavorited = useMemo(() => favoriteHabitats.includes(habitatId), [favoriteHabitats, habitatId]);

  const { post, del, response, loading } = useFetch(buildURL('/habitats/favorite'), {
    credentials: 'include',
    cachePolicy: 'no-cache',
    headers: { 'Content-Type': 'application/json' },
  });

  const likeIcon = useMemo(() => {
    if (loading) {
      return faSpinner;
    }
    if (error) {
      return faTimes;
    }
    if (isFavorited) {
      return faHeart;
    }
    return faHeartOutline;
  }, [isFavorited, loading, error]);

  const onHabitatClick = (evt) => {
    evt.preventDefault();
    evt.stopPropagation();

    onClickPrimaryButton();
    if (!isLogged) {
      logLandingButton(`Habitat-${primaryButtonTitle.replaceAll(' ', '-')}`);
    }
  };

  const onPressTrailer = () => {
    onShowTrailer(trailer);
    if (!isLogged) {
      logLandingButton(`Habitat-Trailer`);
    }
  };

  if (!subscription) {
    return null;
  }

  const defaultOnFavoriteClick = async () => {
    if (isFavorited) {
      await del({ habitatId });
    } else {
      await post({ habitatId });
    }

    if (response.ok) {
      dispatch(toggleFavoriteHabitat(habitatId));
    } else {
      setError(true);
      setTimeout(() => {
        setError(false);
      }, 2000);
    }
  };

  const handleFavoriteClick = () => {
    if (typeof onFavoriteClick === 'undefined') {
      return defaultOnFavoriteClick();
    }
    return onFavoriteClick(habitatId);
  };

  return (
    <HabitatCardBase
      className={className}
      online={online}
      liveTalk={liveTalk}
      image={image}
      logo={logo}
      free={isGeneralUser && !isLocked}
      isFavorited={isFavorited}
      handleFavoriteClick={handleFavoriteClick}
      isLocked={isLocked}
      isLogged={isLogged}
      likeIcon={likeIcon}
      loading={loading}
    >
      <div style={{ flexGrow: 1 }}>
        <div className={style.titleSection}>
          <Link onClick={onHabitatClick} href={encodeURI(`/h/${zooSlug}/${slug}`)}>
            <Text className={style.title} margin={{ vertical: '0px', right: '10px' }}>
              {title}
            </Text>
          </Link>
        </div>
        <Text className={style.description} as="p">
          {description}
        </Text>
      </div>
      <div className={style.buttons}>
        <Box direction="column" justify="between" fill="horizontal" gap="8px">
          {trailer && (
            <NewOutlineButton
              label={
                <Box direction="row" justify="center" align="center">
                  <FontAwesomeIcon icon={faPlay} size="1x" />
                  <Text className={style.buttonText}>View Trailer</Text>
                </Box>
              }
              className={style.iconAndButton}
              onClick={onPressTrailer}
            />
          )}
          {!isLocked && primaryButtonTitle && onClickPrimaryButton && (
            <div onClick={onHabitatClick}>
              <PrimaryButton label={primaryButtonTitle} size="large" className={style.iconAndButton} />
            </div>
          )}
          {isLocked && primaryButtonTitle && onClickPrimaryButton && (
            <PrimaryButton
              onClick={onHabitatClick}
              label={
                <Box direction="row" justify="center" align="center">
                  <FontAwesomeIcon icon={faLockAlt} color="white" size="1x" />
                  <Text className={style.buttonText}>{primaryButtonTitle}</Text>
                </Box>
              }
              className={style.iconAndButton}
            />
          )}
          {displayCloseButton && <NewOutlineButton label="Close" className={style.iconAndButton} onClick={onClose} />}
        </Box>
      </div>
    </HabitatCardBase>
  );
};

export default memo(HabitatCard);
